import { useCallback, useState } from "react";

/**
 * The remount key and saved-text baseline for the uncontrolled answer editor,
 * shared by Venus and Neptune so both decide the same way when the editor reloads.
 *
 * The editor remounts on a new revision (a version pick, a regeneration, another
 * message), except for the revision its own save created: that text is already in
 * the editor, and a remount would re-seed it from server state and drop the caret.
 * The move is the editor's own when the answer it brings is the text it saved.
 * Content, not a "save pending" flag: a flag can stay armed (a save that does not
 * move the pointer) and swallow the next real move.
 *
 * `ownSavedText` is the editor's `savedText`: it resets the dirty state without a
 * remount. A remount for anything else drops it, so the new editor starts from the
 * server text instead of an older save.
 */
export function useAnswerEditorKey({
  messageId,
  currentRevisionId,
  displayedAnswer,
}: {
  messageId: string | null | undefined;
  currentRevisionId: string | null | undefined;
  /** The answer text the editor would be seeded with on a remount. */
  displayedAnswer: string | null | undefined;
}): {
  editorKey: string;
  /** The text this editor last saved on this message, or null. */
  ownSavedText: string | null;
  /** Record a save when it is sent, before its revision comes back. */
  markSaved: (text: string) => void;
} {
  const [saved, setSaved] = useState<{ messageId: string; text: string } | null>(null);
  const ownSavedText = messageId && saved?.messageId === messageId ? saved.text : null;

  // Adjusted during render, the repo's pattern for derived state.
  const revision = `${messageId ?? ""}:${currentRevisionId ?? "tip"}`;
  const [keyState, setKeyState] = useState({ revision, key: revision });
  if (keyState.revision !== revision) {
    const ownSave = ownSavedText !== null && displayedAnswer === ownSavedText;
    setKeyState({ revision, key: ownSave ? keyState.key : revision });
    if (!ownSave && saved !== null) setSaved(null);
  }

  const markSaved = useCallback(
    (text: string) => {
      if (messageId) setSaved({ messageId, text });
    },
    [messageId],
  );

  return { editorKey: keyState.key, ownSavedText, markSaved };
}
